import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Sparkles, ArrowRight, X, Users, ArrowLeftRight, Link2 } from "lucide-react";

/**
 * Global "You have new matches!" popup.
 *
 * Fires (once) whenever the total number of matches (1-on-1 swaps +
 * 3-way chains + Exchange Buddies) is higher than the count the user has
 * already seen — tracked in `localStorage`. Never appears on the /matches
 * page itself (the user is already there). Polls every 45 s and re-checks
 * on tab focus so a match that appears while the app is open is announced
 * promptly.
 *
 * Renders in both the web and Capacitor app so the celebration moment is
 * consistent everywhere.
 */
const SEEN_KEY = "notif:matchesPopupSeenCount";

function readSeen() {
    try {
        const raw = localStorage.getItem(SEEN_KEY);
        const n = raw == null ? null : parseInt(raw, 10);
        return Number.isFinite(n) ? n : null;
    } catch { return null; }
}

function writeSeen(n) {
    try { localStorage.setItem(SEEN_KEY, String(n)); } catch { /* ignore */ }
}

export default function NewMatchPopup() {
    const { user } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const [delta, setDelta] = useState(0);        // # of NEW matches since last seen
    const [total, setTotal] = useState(0);        // current total across all types
    const [breakdown, setBreakdown] = useState({ direct: 0, chain: 0, buddy: 0 });

    const check = useCallback(async () => {
        if (!user) return;
        if (location.pathname.startsWith("/matches")) return; // don't interrupt if already there
        try {
            const [m, c, b] = await Promise.all([
                api.get("/matches"),
                api.get("/matches/chain").catch(() => ({ data: [] })),
                api.get("/matches/buddies").catch(() => ({ data: [] })),
            ]);
            const direct = (m.data || []).length;
            const chain = (c.data || []).length;
            const buddy = (b.data || []).length;
            const currentTotal = direct + chain + buddy;
            const seen = readSeen();

            // First visit ever → don't spam; just set the baseline.
            if (seen == null) {
                writeSeen(currentTotal);
                return;
            }
            if (currentTotal > seen) {
                setDelta(currentTotal - seen);
                setTotal(currentTotal);
                setBreakdown({ direct, chain, buddy });
                setOpen(true);
            }
        } catch { /* silent — the popup is nice-to-have */ }
    }, [user, location.pathname]);

    useEffect(() => {
        if (!user) return;
        check();
        const iv = setInterval(check, 45000);
        const onVis = () => { if (document.visibilityState === "visible") check(); };
        document.addEventListener("visibilitychange", onVis);
        return () => { clearInterval(iv); document.removeEventListener("visibilitychange", onVis); };
    }, [user, check]);

    const dismiss = () => {
        writeSeen(total);
        setOpen(false);
    };

    const goToMatches = () => {
        writeSeen(total);
        setOpen(false);
        navigate("/matches");
    };

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    key="backdrop"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="fixed inset-0 z-[70] bg-foreground/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
                    onClick={dismiss}
                    data-testid="new-match-popup-backdrop"
                    style={{ paddingBottom: "max(1rem, env(safe-area-inset-bottom))" }}
                >
                    <motion.div
                        key="card"
                        initial={{ opacity: 0, y: 40, scale: 0.94 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 20, scale: 0.96 }}
                        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                        onClick={(e) => e.stopPropagation()}
                        data-testid="new-match-popup"
                        className="relative w-full max-w-md rounded-3xl overflow-hidden shadow-2xl bg-white"
                    >
                        {/* Hero — same fresh coral gradient as the other app pages */}
                        <div
                            className="relative p-6 sm:p-7 text-white overflow-hidden"
                            style={{ background: "linear-gradient(135deg, #f4a261 0%, #ee8f6d 45%, #dd6b4d 100%)" }}
                        >
                            <div className="absolute -right-12 -top-12 w-56 h-56 rounded-full bg-white/25 blur-2xl" aria-hidden />
                            <div className="absolute -left-16 -bottom-16 w-56 h-56 rounded-full bg-white/15 blur-2xl" aria-hidden />
                            <button
                                onClick={dismiss}
                                aria-label="Close"
                                data-testid="new-match-popup-close"
                                className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors"
                            >
                                <X className="w-4 h-4" />
                            </button>
                            <div className="relative flex items-start gap-4">
                                <motion.div
                                    initial={{ scale: 0.6, opacity: 0 }}
                                    animate={{ scale: 1, opacity: 1 }}
                                    transition={{ delay: 0.15, duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                                    className="w-14 h-14 rounded-2xl bg-white/95 text-primary flex items-center justify-center shadow-lg shrink-0"
                                >
                                    <Sparkles className="w-7 h-7" strokeWidth={2.2} />
                                </motion.div>
                                <div className="min-w-0">
                                    <div className="text-[11px] font-bold uppercase tracking-[0.25em] text-white/80">
                                        {delta === 1 ? "New match" : "New matches"}
                                    </div>
                                    <h2 className="font-display text-2xl sm:text-3xl font-bold leading-tight mt-1">
                                        {delta === 1 ? "You have a new match!" : `You have ${delta} new matches!`}
                                    </h2>
                                    <p className="text-white/90 mt-1 text-sm">
                                        Someone's looking for what you offer. Check them out.
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Breakdown row + CTA */}
                        <div className="p-5 sm:p-6">
                            <div className="grid grid-cols-3 gap-2 mb-5" data-testid="new-match-popup-breakdown">
                                <BreakdownPill icon={ArrowLeftRight} label="Swap" count={breakdown.direct} />
                                <BreakdownPill icon={Link2} label="Chain" count={breakdown.chain} />
                                <BreakdownPill icon={Users} label="Buddy" count={breakdown.buddy} />
                            </div>
                            <div className="flex flex-col sm:flex-row gap-2">
                                <Button
                                    onClick={goToMatches}
                                    data-testid="new-match-popup-cta"
                                    className="flex-1 h-11 rounded-full bg-primary hover:bg-primary/90 font-semibold"
                                >
                                    See my matches <ArrowRight className="w-4 h-4 ml-1" />
                                </Button>
                                <Button
                                    onClick={dismiss}
                                    variant="ghost"
                                    data-testid="new-match-popup-later"
                                    className="h-11 rounded-full font-medium text-muted-foreground hover:text-foreground"
                                >
                                    Later
                                </Button>
                            </div>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}

function BreakdownPill({ icon: Icon, label, count }) {
    const hasCount = count > 0;
    return (
        <div className={`rounded-2xl px-2.5 py-2.5 text-center border ${hasCount ? "bg-primary/8 border-primary/25" : "bg-muted/60 border-transparent"}`}>
            <div className="flex items-center justify-center gap-1.5">
                <Icon className={`w-3.5 h-3.5 ${hasCount ? "text-primary" : "text-muted-foreground"}`} strokeWidth={2.2} />
                <span className={`text-xs font-bold tabular-nums ${hasCount ? "text-foreground" : "text-muted-foreground"}`}>{count}</span>
            </div>
            <div className={`text-[10px] font-medium uppercase tracking-wider mt-0.5 ${hasCount ? "text-primary" : "text-muted-foreground"}`}>{label}</div>
        </div>
    );
}
